/* eslint-disable no-console, no-unused-vars */

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { VendorPlanFormStyled } from './VendorPlanForm.styled';
import PearlForm from 'vendor/components/forms/PearlForm';
import { VendorAccountSetupStepLayout, VendorAccountSetupStepFooter } from 'framework';
import { useWebstoreContext } from 'vendor/contexts/WebstoreContext/WebstoreContext';
import { useWebstoreSetupWizard } from 'vendor/components/WebstoreSetup/NewWebstoreSetupContext';
import { useTealium, buildWebstoreSetupStepPageData } from '../../../../Tealium';
import { useFormContext } from 'react-hook-form';
import { NewVendorWebstoreSetupHeader } from '../NewVendorWebstoreSetupHeader';
import PickYourPlanSection from 'vendor/components/Webstore/PickYourPlanSection/PickYourPlanSection';
import { TermsOfServiceService } from 'vendor/lib/apis/TermsOfServiceService';
import { unwrapResponse } from '../../../../lib/apis/utils';
import { PickYourPlanSchemaWrapper } from 'vendor/components/Webstore/PickYourPlanSection/PickYourPlanSchema';
import { useVendorContext } from 'vendor/contexts/VendorContext';
import { StripeService } from 'lib/apis/StripeServiceNew';
import { StorageService, StorageKeys } from 'lib/StorageService';
import { useAuthUserContext } from 'contexts/AuthUserContext';
import SignaturePlanChooser from 'vendor/components/Webstore/SignaturePlans/SignaturePlanChooser';

const formName = 'PlanTier';

const Footer = () => {
    const { progress, onBack } = useWebstoreSetupWizard();
    const {
        watch,
        formState: { isDirty },
    } = useFormContext();

    const selectedPlan = watch('SelectedPlan');

    return (
        <VendorAccountSetupStepFooter
            progress={progress}
            handleOnBack={() => onBack()}
            showBack={false}
            pearlConnectFooter
        />
    );
};

const VendorPlanForm = () => {
    const { link, view } = useTealium();
    const { webstore } = useWebstoreContext();
    const { onSubmit, currentStepCount, totalSteps } = useWebstoreSetupWizard();
    const { t } = useTranslation();
    const termsOfServiceApi = TermsOfServiceService();
    const { vendor } = useVendorContext();
    const { authUser } = useAuthUserContext();
    const [starterError, setStarterError] = React.useState('');
    const [isChoosingSignaturePlan, setIsChoosingSignaturePlan] = React.useState(false);
    // Signature Collection vendors choose between Emerald, Ruby and Diamond instead of the
    // standard plans, and have no free tier.
    const isSignature = vendor?.vendorType === 'signature';

    useEffect(() => {
        const stepName = t(`Forms.${formName}.StepName`);
        const tealiumData = buildWebstoreSetupStepPageData(
            stepName,
            webstore,
            currentStepCount,
            totalSteps
        );

        view(tealiumData);
    }, []);

    const defaultValue = webstore.SelectedPlan ?? 'planthree';

    const formOptions = {
        mode: 'onChange',
        defaultValues: { SelectedPlan: defaultValue },
    };

    const handleSubmit = async (data) => {
        data = {
            SelectedPlan: data.SelectedPlan,
        };

        const tealiumData = {
            event_name: 'setup-step-advance-click',
            setup_step_number: 1,
            setup_step_total_step_count: totalSteps,
            setup_step_name: t(`Forms.${formName}.StepName`),
            setup_step_form_data: data,
            setup_step_patch_data: data,
            webstore_id: webstore.Id,
        };

        link(tealiumData);

        // PLAN_TIER_SELECTED used to fire here, but at this point (before
        // payment for paid tiers) the actual renewal_frequency isn't chosen
        // yet, so it had to be guessed. It now fires from the business
        // details step instead -- the wizard's last step, reached after
        // payment for paid tiers -- where vendor.stripeData.priceId is the
        // real, confirmed price (see VendorBusinessDetailsFormNew.js).

        const res = await termsOfServiceApi.putAcceptTermsOfService(vendor.id);
        const tosData = unwrapResponse(res);
        if (tosData?.success) {
            vendor.updateVendor({ tosAccepted: true });
        }

        // Starter flow: create free subscription immediately and skip payment step.
        if (data.SelectedPlan === 'planone') {
            try {
                const storageService = StorageService();
                const stripeService = StripeService();
                let stripeData = [];
                const localStripeData = storageService.get('stripeData');
                if (localStripeData) {
                    stripeData = JSON.parse(localStripeData);
                } else {
                    const response = await stripeService.getStripeProducts();
                    stripeData = response?.data || [];
                    storageService.set('stripeData', JSON.stringify(stripeData));
                }

                const starterPrice = stripeData.find(
                    (item) => item.planTier === 'planone' && item.billingFrequency === 'month'
                );
                if (!starterPrice?.stripePriceId) {
                    throw new Error('Starter plan price is not configured.');
                }

                const starterResponse = await stripeService.createStarterSubscription(
                    vendor.name || authUser?.Username || 'Starter Subscription',
                    starterPrice.stripePriceId,
                    authUser?.Email,
                    vendor.id
                );
                vendor.updateVendor({
                    stripeData: {
                        ...(vendor.stripeData || {}),
                        subscriptionId: starterResponse?.data?.subscriptionId,
                        priceId: starterPrice.stripePriceId,
                        total: '0.00',
                    },
                });
                setStarterError('');
            } catch (error) {
                setStarterError(
                    error?.message || 'Unable to create starter subscription. Please try again.'
                );
                return;
            }
        }
        onSubmit(data);
    };

    // Checkout reads its plan rows from storage ('stripeData') and finds the price by plan tier
    // and frequency. The Signature plans share one tier, so only the chosen row is kept: checkout
    // then prices exactly that plan, annually, under its own name.
    const handleSignaturePlanChoose = async (plan) => {
        setIsChoosingSignaturePlan(true);
        try {
            const storageService = StorageService();
            storageService.set(
                'stripeData',
                JSON.stringify([{ ...plan.row, tierName: plan.row.displayName }])
            );
            storageService.set(StorageKeys.planBillingFrequency, 'year');
            await handleSubmit({ SelectedPlan: plan.row.planTier });
        } finally {
            setIsChoosingSignaturePlan(false);
        }
    };

    const PickYourPlanSchema = PickYourPlanSchemaWrapper();

    return (
        <PearlForm
            StyledForm={VendorPlanFormStyled}
            formName={formName}
            formOptions={formOptions}
            onSubmit={(data) => handleSubmit(data)}
            validationSchema={PickYourPlanSchema}
        >
            <VendorAccountSetupStepLayout
                header={
                    <NewVendorWebstoreSetupHeader
                        stepName={isSignature ? 'SignaturePlan' : formName}
                        currentStepCount={currentStepCount}
                        totalSteps={totalSteps - 1}
                        showMobileTopBar
                        pearlConnectStepTheme
                        pearlConnectSubtitleAccent
                    />
                }
                formComponent={
                    isSignature ? (
                        <SignaturePlanChooser
                            onChoose={handleSignaturePlanChoose}
                            isBusy={isChoosingSignaturePlan}
                        />
                    ) : (
                        <PickYourPlanSection
                            defaultPlanValue={defaultValue}
                            pearlConnectPlanLayout
                        />
                    )
                }
                footer={isSignature ? null : <Footer />}
                noFooterSpace={isSignature}
                compactHeader={isSignature}
            />
            {starterError ? <div className="error-text">{starterError}</div> : null}
        </PearlForm>
    );
};

export { VendorPlanForm };
